import React, { useState, useEffect } from 'react';
import ResourceNavNode from './ResourceNavNode';
import TokenManager from '../utils/tokenManager';
import { useTenants } from '../hooks/useTenants';
import './ResourceNav.scss';

interface ResourceNavProps {
    onTenantSelect?: (tenantId: string) => void;
    isMobileOverlay?: boolean;
    onClose?: () => void;
}

const ResourceNav: React.FC<ResourceNavProps> = ({ onTenantSelect, isMobileOverlay = false, onClose }) => {
    const tokenManager = TokenManager.getInstance();
    const isAuthenticated = tokenManager.isAuthenticated();
    const { data: tenants = [], isLoading } = useTenants();
    const [isMobile, setIsMobile] = useState(false);
    const [isMenuOpen, setIsMenuOpen] = useState(false);

    useEffect(() => {
        const checkMobile = () => setIsMobile(window.innerWidth <= 768);
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    useEffect(() => {
        if (isMobile && isMenuOpen && !isMobileOverlay) {
            const handleClickOutside = (event: MouseEvent) => {
                const target = event.target as Element;
                if (!target.closest('[data-menu="true"]')) {
                    setIsMenuOpen(false);
                }
            };
            document.addEventListener('click', handleClickOutside);
            return () => document.removeEventListener('click', handleClickOutside);
        }
    }, [isMobile, isMenuOpen, isMobileOverlay]);

    if (isMobileOverlay || (isMobile && !isMobileOverlay)) {
        const showMenu = isMobileOverlay || isMenuOpen;

        return (
            <>
                {!isMobileOverlay && (
                    <button
                        id="mobile-menu-button"
                        className="resource-nav-mobile-button"
                        onClick={() => setIsMenuOpen(!isMenuOpen)}
                        data-menu="true"
                    >
                        ☰
                    </button>
                )}
                {showMenu && (
                    <div
                        id="mobile-menu-overlay"
                        className="resource-nav-mobile-overlay"
                        data-menu="true"
                    >
                        <button
                            id="mobile-menu-close"
                            className="resource-nav-close-button"
                            onClick={() => {
                                if (isMobileOverlay && onClose) {
                                    onClose();
                                } else {
                                    setIsMenuOpen(false);
                                }
                            }}
                        >
                            ×
                        </button>
                        <div className="resource-nav-content">
                            <h3 className="resource-nav-title">Resources</h3>
                            {!isAuthenticated ? (
                                <div className="resource-nav-auth-message">
                                    Please sign in to view your resources
                                </div>
                            ) : (
                                <>
                                    {isLoading ? (
                                        <div className="resource-nav-loading">Loading...</div>
                                    ) : (
                                        <>
                                            <ResourceNavNode
                                                resourceName="Tenants"
                                                resourcePath="/tenants"
                                                items={tenants.map(tenant => ({
                                                    ...tenant,
                                                    subResources: [
                                                        {
                                                            name: 'Environments',
                                                            path: `/tenants/${tenant.id}/environments`
                                                        }
                                                    ]
                                                }))}
                                                onItemSelect={onTenantSelect}
                                            />
                                            <ResourceNavNode
                                                resourceName="Catalogs"
                                                resourcePath="/catalogs"
                                                items={[]}
                                            />
                                            <ResourceNavNode
                                                resourceName="Connections"
                                                resourcePath="/connections"
                                                items={[
                                                    { id: 'github', name: 'GitHub' },
                                                    { id: 'aws', name: 'AWS' }
                                                ]}
                                                showItemCount={false}
                                            />
                                        </>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                )}
            </>
        );
    }

    return (
        <div id="resource-nav-desktop" className="resource-nav-desktop">
            <h3 className="resource-nav-title">Resources</h3>

            {!isAuthenticated ? (
                <div className="resource-nav-auth-message">
                    Please sign in to view your resources
                </div>
            ) : (
                <>
                    {isLoading ? (
                        <div className="resource-nav-loading">Loading...</div>
                    ) : (
                        <>
                            <ResourceNavNode
                                resourceName="Tenants"
                                resourcePath="/tenants"
                                items={tenants.map(tenant => ({
                                    ...tenant,
                                    subResources: [
                                        {
                                            name: 'Environments',
                                            path: `/tenants/${tenant.id}/environments`
                                        }
                                    ]
                                }))}
                                onItemSelect={onTenantSelect}
                            />
                            <ResourceNavNode
                                resourceName="Catalogs"
                                resourcePath="/catalogs"
                                items={[]}
                            />
                            <ResourceNavNode
                                resourceName="Connections"
                                resourcePath="/connections"
                                items={[
                                    { id: 'github', name: 'GitHub' },
                                    { id: 'aws', name: 'AWS' }
                                ]}
                                showItemCount={false}
                            />
                        </>
                    )}
                </>
            )}
        </div>
    );
};

export default ResourceNav;